import {
  Component,
  EventEmitter,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { GetMyDataUseCase } from 'src/app/features/user/domain/usecases/get-my-data.usecase';
import { Location } from '@angular/common';
import { Toast } from 'primeng/toast';
import { Failure } from 'src/app/core/utils/failure';
import { ClaimMultiModel } from 'src/app/features/claims/data/models/claim-multi.model';
import { CreateClaimMultiUseCase } from 'src/app/features/claims/domain/usecases/create-claim-multi.usecase';
import { ClaimMultiByIdUseCase } from 'src/app/features/claims/domain/usecases/get-claim-id-multi.usecase';
import { UpdateClaimMultiUseCase } from 'src/app/features/claims/domain/usecases/update-claim-multi.usecase';

@Component({
  selector: 'app-claim-multi-single',
  templateUrl: './claim-multi-single.component.html',
  styleUrls: ['./claim-multi-single.component.css'],
  providers: [MessageService, Toast, ConfirmationService],
})
export class ClaimMultiSingleComponent {
  claim?: any;
  isNewClaim: boolean = false;
  activeIndex: number = 0;
  claimForm!: FormGroup;
  userData?: UserData;
  isLoading: boolean = false;
  claimId?: string | null;
  isLoadingEdit: boolean = false;
  isEdit: boolean = false;

  stepPanels: {
    label: string;
    icon: string;
    contentTemplate: TemplateRef<any>;
  }[] = [];

  @ViewChild('repTemplate', { static: true })
  repTemplate!: TemplateRef<any>;
  @ViewChild('claimantTemplate', { static: true })
  claimantTemplate!: TemplateRef<any>;
  @ViewChild('claimedTemplate', { static: true })
  claimedTemplate!: TemplateRef<any>;
  @ViewChild('claimDetailsTemplate', { static: true })
  claimDetailsTemplate!: TemplateRef<any>;

  isRepresentative: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private myData: GetMyDataUseCase,
    private messageService: MessageService,
    private createClaimUseCase: CreateClaimMultiUseCase,
    private router: Router,
    private route: ActivatedRoute,
    private claimUseCase: ClaimMultiByIdUseCase,
    private updateClaimUseCase: UpdateClaimMultiUseCase,
    private location: Location,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.stepPanels = [];
    this.initForm();
    this.checkIsNew();
  }

  private initForm(): void {
    this.claimForm = this.formBuilder.group({
      userId: ['', Validators.required],
      rol: ['', Validators.required],
      id: ['', Validators.required],
      status: ['', Validators.required],
      claimType: ['', Validators.required],
      numberOfClaim: ['', Validators.required],
      startDate: ['', Validators.required],
      isBorrador: [false, Validators.required],
    });
  }

  async checkIsNew() {
    await this.getMyData();
    if (this.router.url.includes('main/new')) {
      this.isNewClaim = true;
    } else {
      this.claimId = this.route.snapshot.paramMap.get('id');
      this.getClaim();
    }
  }

  async getMyData(): Promise<void> {
    let result = await this.myData.execute();
    if (result instanceof Failure) {
      return;
    }
    this.userData = result;
    this.isRepresentative = this.userData.rol === 'Representante gremial';

    if (this.isRepresentative) {
      this.stepPanels.push({
        label: 'Datos del representante',
        icon: 'pi pi-user',
        contentTemplate: this.repTemplate,
      });
    }
    this.stepPanels.push(
      {
        label: this.isRepresentative
          ? 'Datos del reclamado'
          : 'Datos del reclamante',
        icon: 'pi pi-user',
        contentTemplate: this.claimantTemplate,
      },
      {
        label: this.isRepresentative
          ? 'Datos de representados'
          : 'Datos de reclamados',
        icon: 'pi pi-users',
        contentTemplate: this.claimedTemplate,
      },
      {
        label: 'Detalles del reclamo',
        icon: 'pi pi-file',
        contentTemplate: this.claimDetailsTemplate,
      }
    );
  }

  async getClaim(): Promise<void> {
    if (!this.claimId) return;
    this.isLoadingEdit = true;
    let result = await this.claimUseCase.execute(this.claimId!);
    if (result instanceof Failure) {
      this.isLoadingEdit = false;
      return;
    }
    this.claim = result;
    this.isEdit = true;
    this.isLoadingEdit = false;
  }

  receiveData(data: any) {
    if (!data || typeof data !== 'object') return;
    if ('controls' in data && typeof data.controls === 'object') {
      Object.keys(data.controls).forEach((key) => {
        if (!this.claimForm.contains(key)) {
          this.claimForm.addControl(key, data.controls[key]);
        }
      });
    } else {
      Object.keys(data).forEach((key) => {
        if (!this.claimForm.contains(key)) {
          this.claimForm.addControl(key, new FormControl(data[key]));
        }
      });
    }
  }

  formComplete(data?: any) {
    let formGroup: FormGroup;
    const controls: { [key: string]: AbstractControl } = {};
    Object.keys(data).forEach((key) => {
      controls[key] = new FormControl(data[key]);
    });
    formGroup = new FormGroup(controls);

    Object.keys(formGroup.controls).forEach((key) => {
      if (!this.claimForm.contains(key)) {
        this.claimForm.addControl(key, formGroup.controls[key]);
      }
    });
    if (this.isNewClaim) {
      this.createClaim();
    } else if (
      this.claim?.status === 'Pendiente' ||
      this.claim?.status === 'Borrador'
    ) {
      this.updateClaim();
    }
  }

  async updateClaim(): Promise<void> {
    this.loadData();
    this.setFilesToSupportFormat();
    this.isLoading = true;
    try {
      let result = await this.updateClaimUseCase.execute(this.createNewClaim());
      if (result instanceof Failure) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
        return;
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Reclamo actualizado',
        detail: 'Su reclamo se actualizó exitosamente.',
      });
      setTimeout(() => {
        this.router.navigate(['/main/home']);
      }, 2000);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error inesperado',
        detail: 'Ha ocurrido un error. Inténtelo nuevamente.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  activeStep(index: number) {
    this.activeIndex = index;
  }

  nextStep(): void {
    if (this.activeIndex < 2) {
      this.activeIndex++;
    }
  }

  async createClaim(): Promise<void> {
    this.isLoading = true;
    this.loadData();
    try {
      let result = await this.createClaimUseCase.execute(this.createNewClaim());
      if (result instanceof Failure) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
        });
        return;
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Reclamo creado',
        detail: 'Su nuevo reclamo se creó exitosamente.',
      });
      setTimeout(() => {
        this.router.navigate(['/main/home']);
      }, 1100);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error inesperado',
        detail: 'Ha ocurrido un error. Inténtelo nuevamente.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  loadData() {
    this.claimForm.get('rol')?.setValue(this.userData?.rol);
    this.claimForm.get('userId')?.setValue(this.userData?.id);
    if (this.claim?.status !== 'Borrador' || this.claim?.status !== undefined) {
      this.claimForm.get('id')?.setValue(this.claim?.id);
      this.claimForm.get('status')?.setValue(this.claim?.status);
      this.claimForm.get('claimType')?.setValue(this.claim?.claimType);
      this.claimForm.get('numberOfClaim')?.setValue(this.claim?.numberOfClaim);
      this.claimForm
        .get('confirmInformation')
        ?.setValue(this.claim?.confirmInformation);
      this.claimForm.get('isBorrador')?.setValue(this.claim?.isBorrador);
      this.claimForm.get('startDate')?.setValue(this.claim?.startDate);
    }
  }

  cancel(isActive: boolean) {
    if (this.claim?.status === 'Borrador' || this.claim?.status === undefined) {
      if (!isActive) return;
      this.confirmationService.confirm({
        header: 'Salir del reclamo',
        message:
          'Su progreso se guardara automáticamente como borrador si desea salir. Podrá retomar cuando lo desee.',
        accept: async () => {
          try {
            await this.saveDraft(this.claimForm.value);
          } catch (error) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail:
                'No se pudo guardar el borrador. Por favor intente nuevamente.',
            });
          }
        },
      });
    } else {
      this.confirmationService.confirm({
        header: 'Salir del reclamo',
        message:
          '¿Estás seguro de que deseas salir del reclamo? Se perderán los cambios no guardados.',
        accept: async () => {
          this.router.navigate([this.location.back()]);
        },
      });
    }
  }

  async saveDraft(data: FormGroup): Promise<void> {
    this.isLoading = true;
    try {
      const originalValidators = new Map();
      Object.keys(this.claimForm.controls).forEach((key) => {
        originalValidators.set(key, this.claimForm.get(key)?.validator);
        this.claimForm.get(key)?.clearValidators();
        this.claimForm.get(key)?.updateValueAndValidity({ emitEvent: false });
      });
      this.claimForm.patchValue(data);
      this.loadData();
      this.claimForm.patchValue({
        ...data.value,
        isBorrador: true,
      });

      this.setFilesToSupportFormat();
      let result;
      if (this.claimId) {
        this.claimForm.patchValue({ id: this.claimId });
        result = await this.updateClaimUseCase.execute(this.createNewClaim());
      } else {
        result = await this.createClaimUseCase.execute(this.createNewClaim());
      }

      if (result instanceof Failure) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message || 'Ocurrió un error al guardar el borrador',
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Guardado',
        detail: this.claimId
          ? 'Borrador actualizado exitosamente'
          : 'Borrador guardado exitosamente',
        life: 3000,
      });

      setTimeout(() => {
        this.router.navigate(['/main/claims']);
      }, 1100);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error al guardar el borrador',
      });
    } finally {
      this.isLoading = false;
    }
  }

  setFilesToSupportFormat() {
    const handleFileOrBase64 = (value: any): string[] => {
      const result: string[] = [];
      if (!value) return result;

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === 'string' && item.includes('base64')) {
            result.push(item);
          } else if (item?.base64) {
            result.push(item.base64);
          } else if (typeof item === 'string') {
            result.push(item);
          }
        });
      } else if (typeof value === 'string' && value.includes('base64')) {
        result.push(value);
      } else if (value?.base64) {
        result.push(value.base64);
      } else if (typeof value === 'string') {
        result.push(value);
      }
      return result;
    };

    const extraDocFiles = handleFileOrBase64(
      this.claimForm.get('extraDocumentationPdf')?.value
    );

    if (extraDocFiles.length > 0) {
      this.claimForm.get('extraDocumentationPdf')?.setValue(extraDocFiles);
    }
  }

  createNewClaim() {
    const formControls = [
      'id',
      'userId',
      'rol',
      'razonSocial',
      'companyCuit',
      'companyEmail',
      'companyPhone',
      'streetCompany',
      'numberCompany',
      'floorApartmentCompany',
      'towerCompany',
      'postalCodeCompany',
      'provinceCompany',
      'cityCompany',
      'claimantTypePerformance',
      'departmentId',
      'lastName',
      'name',
      'cuil',
      'claimantPosition',
      'phone',
      'email',
      'birthday',
      'file',
      'employees',
      'selecterObjectOfClaim',
      'textClaimDetails',
      'extraDocumentationPdf',
      'selectedOwnRight',
      'lawyerFullName',
      'lawyerRegistrationNumber',
      'lawyerEmail',
      'consent',
      'confirmInformation',
      'claimType',
      'startDate',
      'numberOfClaim',
      'isBorrador',
      'status',
      'conciliatorName',
      'audience_id',
      'date_audience',
      'time_audience',
      'type_of_person',
      'representative_last_name',
      'representative_name',
      'representative_cuil',
      'representative_email',
      'representative_phone',
      'representative_street',
      'representative_number',
      'representative_floor_apartment',
      'representative_tower',
      'representative_postal_code',
      'representative_province',
      'representative_city',
    ];

    formControls.forEach((control) => {
      const value = this.claimForm.get(control)?.value;
      try {
        JSON.stringify(value);
      } catch (e) {
        console.error(`El control "${control}" no es serializable:`, value, e);
      }

      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null
      ) {
        console.warn(
          `El control "${control}" parece ser un objeto complejo:`,
          value
        );
      }
    });

    return new ClaimMultiModel(
      this.claimForm.get('id')?.value ?? '',
      this.claimForm.get('userId')?.value ?? '',
      this.claimForm.get('rol')?.value ?? '',
      this.claimForm.get('razonSocial')?.value ?? '',
      this.claimForm.get('companyCuit')?.value ?? '',
      this.claimForm.get('companyEmail')?.value ?? '',
      this.claimForm.get('companyPhone')?.value ?? '',
      this.claimForm.get('streetCompany')?.value ?? '',
      this.claimForm.get('numberCompany')?.value?.toString() ?? '',
      this.claimForm.get('floorApartmentCompany')?.value ?? '',
      this.claimForm.get('towerCompany')?.value ?? '',
      this.claimForm.get('postalCodeCompany')?.value?.toString() ?? '',
      this.claimForm.get('provinceCompany')?.value ?? '',
      this.claimForm.get('cityCompany')?.value ?? '',
      this.claimForm.get('claimantTypePerformance')?.value ?? '',
      this.claimForm.get('departmentId')?.value === 'Otro' ||
      this.claimForm.get('departmentId')?.value == null
        ? ''
        : this.claimForm.get('departmentId')?.value,
      this.claimForm.get('lastName')?.value ?? '',
      this.claimForm.get('name')?.value ?? '',
      this.claimForm.get('cuil')?.value ?? '',
      this.claimForm.get('claimantPosition')?.value ?? '',
      this.claimForm.get('phone')?.value ?? '',
      this.claimForm.get('email')?.value ?? '',
      this.claimForm.get('birthday')?.value ?? null,
      this.claimForm.get('file')?.value ?? [],
      this.claimForm.get('employees')?.value ?? [],
      this.claimForm.get('selecterObjectOfClaim')?.value ?? [],
      this.claimForm.get('textClaimDetails')?.value ?? '',
      this.claimForm.get('extraDocumentationPdf')?.value ?? [],
      this.claimForm.get('selectedOwnRight')?.value ?? '',
      this.claimForm.get('lawyerFullName')?.value ?? '',
      this.claimForm.get('lawyerRegistrationNumber')?.value ?? '',
      this.claimForm.get('lawyerEmail')?.value ?? '',
      this.claimForm.get('consent')?.value ?? false,
      this.claimForm.get('confirmInformation')?.value ?? false,
      this.claimForm.get('claimType')?.value ?? '',
      this.claimForm.get('startDate')?.value ?? '',
      this.claimForm.get('numberOfClaim')?.value ?? '',
      this.claimForm.get('isBorrador')?.value ?? false,
      this.claimForm.get('status')?.value ?? '',
      this.claimForm.get('conciliatorName')?.value ?? '',
      this.claimForm.get('audience_id')?.value ?? '',
      this.claimForm.get('date_audience')?.value ?? '',
      this.claimForm.get('time_audience')?.value ?? '',
      this.claimForm.get('type_of_person')?.value ?? '',
      this.claimForm.get('representative_last_name')?.value ?? '',
      this.claimForm.get('representative_name')?.value ?? '',
      this.claimForm.get('representative_cuil')?.value ?? '',
      this.claimForm.get('representative_email')?.value ?? '',
      this.claimForm.get('representative_phone')?.value ?? '',
      this.claimForm.get('representative_street')?.value ?? '',
      this.claimForm.get('representative_number')?.value?.toString() ?? '',
      this.claimForm.get('representative_floor_apartment')?.value ?? '',
      this.claimForm.get('representative_tower')?.value ?? '',
      this.claimForm.get('representative_postal_code')?.value?.toString() ?? '',
      this.claimForm.get('representative_province')?.value ?? '',
      this.claimForm.get('representative_city')?.value ?? ''
    );
  }
}
